import { NetworkError } from "./errors";

export interface OxyLabsConfig {
  apiKey?: string;
  username?: string;
  password?: string;
  country?: string;
  device?: "desktop" | "mobile" | "tablet";
  renderJs?: boolean;
  sessionId?: string;
}

export class OxyLabsService {
  private username: string;
  private password: string;
  private baseUrl = "https://realtime.oxylabs.io/v1/queries";
  private defaultConfig: Partial<OxyLabsConfig>;

  constructor(config: OxyLabsConfig) {
    // Oxylabs uses username/password authentication
    this.username = config.username || config.apiKey || "";
    this.password = config.password || config.apiKey || "";
    this.defaultConfig = {
      country: config.country || "us",
      device: config.device || "desktop",
      renderJs: config.renderJs !== undefined ? config.renderJs : true,
      sessionId: config.sessionId,
    };
  }

  /**
   * Fetches a URL through the Oxylabs proxy
   */
  async fetch(
    targetUrl: string,
    options?: Partial<OxyLabsConfig>,
  ): Promise<Response> {
    try {
      const payload = {
        source: "universal",
        url: targetUrl,
        render: this.defaultConfig.renderJs || options?.renderJs,
        geo_location: this.defaultConfig.country || options?.country || "us",
        device: this.mapDeviceType(
          this.defaultConfig.device || options?.device,
        ),
        session_id: this.defaultConfig.sessionId || options?.sessionId,
      };

      // Basic auth credentials
      const credentials = btoa(`${this.username}:${this.password}`);

      const response = await fetch(this.baseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${credentials}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new NetworkError(
          `Oxylabs request failed with status ${response.status}: ${response.statusText}`,
        );
      }

      return response;
    } catch (error) {
      if (error instanceof NetworkError) throw error;
      throw new NetworkError(
        `Oxylabs request failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Maps device types to Oxylabs format
   */
  private mapDeviceType(device?: "desktop" | "mobile" | "tablet"): string {
    switch (device) {
      case "mobile":
        return "smartphone";
      case "tablet":
        return "tablet";
      case "desktop":
      default:
        return "desktop";
    }
  }

  /**
   * Fetches HTML content from a URL through the Oxylabs proxy
   */
  async fetchHtml(
    targetUrl: string,
    options?: Partial<OxyLabsConfig>,
  ): Promise<string> {
    const response = await this.fetch(targetUrl, options);
    const data = await response.json();
    return data.results?.[0]?.content || "";
  }

  /**
   * Fetches JSON from a URL through the Oxylabs proxy
   */
  async fetchJson<T>(
    targetUrl: string,
    options?: Partial<OxyLabsConfig>,
  ): Promise<T> {
    const response = await this.fetch(targetUrl, options);
    const data = await response.json();
    return data.results?.[0]?.content as T;
  }

  /**
   * Test the connection to Oxylabs
   */
  async testConnection(): Promise<boolean> {
    try {
      // Use a simple test URL
      const testUrl = "https://httpbin.org/ip";
      const response = await this.fetch(testUrl);
      const data = await response.json();

      // If we get a successful response with results, the connection is working
      return !!data.results && data.results.length > 0;
    } catch (error) {
      console.error("Oxylabs connection test failed:", error);
      return false;
    }
  }
}
